"use client";

import Icon from "@/components/icons/Icons";
import CustomDropdown from "@/components/ui/CustomDropdown";
import { CityItem } from "@/type/goldPageType";
import { formatIndianNumber } from "@/utils/commonFunctions";
import { getCityGoldPrices } from "@/utils/goldPageFunctions";
import Link from "next/link";
import Image from "next/image";
import { useRef, useState } from "react";
import { ROOT_URL } from "@/utils/gloableVariables";

type FundItem = {
  id?: number;
  scheme_name?: string;
  scheme_code?: string;
  amc_code?: string;
  amc?: string;
  url?: string;
  category_type?: string;
  asset_id?: number;
  sip_allowed?: number;
  lump_allowed?: number;
  minimum_sip_invest?: number;
  minimum_investment?: number;
  six_month_abs?: number;
  one_year_abs?: number;
  three_year?: number;
};

export default function GoldRatesCitiesTable({
  schemeData,
  cities,
}: {
  schemeData: FundItem[];
  cities: CityItem[];
}) {
  const [search, setSearch] = useState("");
  const dropdownRef = useRef<HTMLButtonElement>(null);
  const [purity, setPurity] = useState<24 | 22 | 18>(24);
  const [openPurity, setOpenPurity] = useState(false);

  const purityOptions: (24 | 22 | 18)[] = [24, 22, 18];

  if (!schemeData || !cities) return;

  const filteredFunds = schemeData.filter(
    (fund) => fund.sip_allowed !== 0 || fund.lump_allowed !== 0,
  );

  const sortedFunds = filteredFunds.sort(
    (a, b) => (b?.one_year_abs ?? 0) - (a?.one_year_abs ?? 0),
  );

  // Cities Filtered Data

  const cityPrices = getCityGoldPrices(cities, purity);

  const filteredCities = cityPrices.filter((item) =>
    item.city.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <section className="section mb-16 sm:mb-100 flex flex-col lg:flex-row lg:items-end gap-16 sm:gap-100 lg:gap-5 relative z-1">
      <div className="w-full max-w-3xl mx-auto">
        {/* Heading + Search */}
        <div className="flex flex-col xxs:flex-row items-center gap-4 xxs:gap-0 justify-between mb-3">
          <h2 className="px-2.5 sm:px-0 text-xl sm:text-2xl font-bold text-left text-white">
            Gold Price in India
          </h2>
          {/* Search Input */}
          <label className="block relative">
            <input
              type="text"
              placeholder="Search City"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="bg-black/30 text-white border border-[#b3980091] focus:border-[#B19600] rounded-full px-4 py-1.5 xxs:py-2 pl-10 placeholder-gray-300 w-44 focus:outline-hidden"
            />
            <Icon.searchIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-white/80" />
          </label>
        </div>

        {/* Table Container */}
        <div className="bg-transparent rounded-2xl border border-gray-700  overflow-y-auto h-115 scrollbar-none">
          {/* Header */}
          <div className="sticky top-0 flex items-center justify-between px-5 py-3 bg-[#1a1a1a] text-gray-300 text-sm font-medium">
            <span>CITY</span>

            <div className="w-full max-w-fit relative">
              <button
                ref={dropdownRef}
                onClick={() => setOpenPurity(!openPurity)}
                aria-label="Select the Gold karat"
                className="w-full flex items-center gap-5 bg-white/10 border border-[#b3980091] focus:border-[#B19600] px-5 py-1.5 rounded-full text-white"
              >
                Rate ({purity}K - 10G)
                <Icon.dropdownDownArrow
                  className={`w-3.5 transition duration-300 ${openPurity && "rotate-180"}`}
                />
              </button>

              <CustomDropdown
                isOpen={openPurity}
                onClose={() => setOpenPurity(false)}
                excludeRefs={[dropdownRef]}
                className="absolute top-full left-0 w-full mt-1 rounded-xl border border-[#B19600] bg-white/10 backdrop-blur-md shadow-md z-10 p-2"
              >
                <div className="space-y-1 max-h-60 overflow-y-auto scrollbar-xs">
                  {purityOptions.map((p) => (
                    <button
                      key={p}
                      onClick={() => {
                        setPurity(p);
                        setOpenPurity(false);
                      }}
                      className="w-full text-left px-3 py-2 text-white hover:bg-white/10 rounded-xl transition"
                    >
                      Rate ({p}K - 10G)
                    </button>
                  ))}
                </div>
              </CustomDropdown>
            </div>
          </div>

          {/* Rows */}
          {filteredCities.map((item, index) => (
            <div key={index} className="grid grid-cols-2 px-5 py-4 text-white">
              <Link
                href={`/buy-digital-gold/gold-price/${item.city.toLowerCase()}`}
                className="transition hover:text-[#b19600]"
              >
                {item.city}
              </Link>
              <span className="flex items-center justify-end gap-3">
                ₹{formatIndianNumber(item.price)}
              </span>
            </div>
          ))}

          {filteredCities.length === 0 && (
            <p className="text-center py-6 text-gray-400">No cities found</p>
          )}
        </div>
      </div>

      <div className="w-full lg:max-w-118.5 mx-auto">
        {/* Header */}
        <h2 className="px-2.5 sm:px-0 text-xl sm:text-2xl font-bold text-center xxs:text-left text-white mb-4">
          Top Gold Mutual funds in India
        </h2>

        {/* Table Container */}
        <div className="bg-transparent rounded-2xl border border-gray-700 overflow-y-auto h-115 scrollbar-none">
          {/* Table Header */}
          <div className="sticky top-0 grid grid-cols-2 px-5 py-3 bg-[#1a1a1a] text-gray-300 text-sm font-medium">
            <span>FUNDS</span>
            <span className="ml-auto text-left">RETURNS (1Y)</span>
          </div>

          {/* Table Rows */}
          {sortedFunds.map((fund, index) => {
            const oneYearRtn = fund.one_year_abs ?? 0;

            return (
              <div
                key={index}
                className="grid grid-cols-2 px-5 py-4 text-white"
              >
                {/* Fund Name */}
                <Link
                  href={`/mutual-fund/${fund.url}/mso${fund.id}`}
                  className="group flex items-center gap-2"
                >
                  <Image
                    src={`${ROOT_URL}img/AMCsLogo/${fund.amc_code}.png`}
                    alt={fund?.scheme_name || "Fund Icon"}
                    width={40}
                    height={40}
                    className="w-7 h-7 overflow-hidden shrink-0 border border-[#E9E9EB] rounded-full group-hover:border-[#b19600]"
                  />
                  <span className="leading-1.5 line-clamp-2 transition group-hover:text-[#b19600]">
                    {fund.scheme_name}
                  </span>
                </Link>

                {/* 1Y Return */}
                <div className="flex items-center gap-2 justify-end">
                  <span
                    className={
                      oneYearRtn > 0
                        ? "text-secondaryGreen"
                        : oneYearRtn === 0
                          ? "text-white"
                          : "text-red-500"
                    }
                  >
                    {oneYearRtn > 0
                      ? `+${oneYearRtn}%`
                      : oneYearRtn < 0
                        ? `-${oneYearRtn}%`
                        : 0}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
